import { MathUtils, Path, Shape } from "three";
import type { Input } from "../../../../../types/shape";
import {
	type ThreeInputLinesType,
	ThreeShapeSideTypes,
	type ThreeSidePathsType,
} from "../../../../../types/three";
import type { DimensionProperties } from "../../../../../types/workpiece";
import { getMaxBasedOnUnit } from "../../../../utils";
import type InputType from "../../../Input/InputType";
import { JsonInputType } from "../../../Input/JsonInputType";
import { SideName } from "../../../Side/SideName";
import Circle from "../Circle";

export default class Half extends Circle {
	public static code = "plate_circle_half";

	public calculateSideLength(side: SideName, input: InputType) {
		const diameter = Number(input[JsonInputType.Diameter]);

		if (!Number.isNaN(diameter)) {
			switch (side) {
				case SideName.OuterEdge:
					return (diameter * Math.PI) / 2;
				case SideName.Length1:
					return diameter;
			}
		}

		return undefined;
	}

	public calculateSheetDimensions(input: InputType): { length: number; width: number } | undefined {
		const diameter = Number(input[JsonInputType.Diameter]);

		if (!Number.isNaN(diameter)) {
			return { length: diameter / 2, width: diameter };
		}
	}

	public getConstraints(
		input: InputType,
		current: Input,
		_equalCorners?: boolean,
	): number | undefined {
		const length = Number(input[JsonInputType.Length] ?? 0);
		const width = Number(input[JsonInputType.Width] ?? 0);

		const panels = this.product?.panelSizes?.filter(
			(panel) => length <= panel.length && width <= panel.width,
		);

		const maxLength = panels?.reduce(
			(prev, current) => (current.saleLength > prev ? current.saleLength : prev),
			0,
		);

		const maxWidth = panels?.reduce(
			(prev, current) => (current.saleWidth > prev ? current.saleWidth : prev),
			0,
		);

		const max = current.max ? getMaxBasedOnUnit(current.max, this.unit) : 0;
		const panelLength = Math.min(max, maxLength ?? 0);
		const panelWidth = Math.min(max, maxWidth ?? 0);

		return Math.min(panelLength, panelWidth * 2, max);
	}

	public getThreeShape(props: DimensionProperties["plate_circle_half"]) {
		const [width] = [props.width / 1000];

		const radius = width / 2;
		const s = new Shape();

		s.moveTo(radius, 0);
		s.arc(0, 0, radius, 0, Math.PI, false);
		s.moveTo(radius, 0);
		return s;
	}

	public getThreeSidePaths(props: DimensionProperties["plate_circle_half"]): ThreeSidePathsType[] {
		const [width] = [props.width / 1000];

		return [
			{
				path: new Path()
					.moveTo(width, 0)
					.arc(-width / 2, 0, width / 2, MathUtils.degToRad(0), MathUtils.degToRad(180), false),
				type: ThreeShapeSideTypes.side,
				code: "length2",
			},
			{
				path: new Path().moveTo(0, 0).lineTo(width, 0),
				code: "length1",
				type: ThreeShapeSideTypes.side,
			},
		];
	}

	public getThreeInputLines(
		props: DimensionProperties["plate_circle_half"],
	): ThreeInputLinesType[] {
		const [width] = [props.width / 1000];

		return [
			{
				dimension: props.width,
				path: new Path().moveTo(0, 0).lineTo(width, 0),
				type: ThreeShapeSideTypes.side,
				code: "width",
			},
		];
	}

	public getDimensions(props: DimensionProperties["plate_circle_half"]) {
		return {
			width: props.width ? props.width : 1000,
		};
	}
}
